import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import { ChevronDown } from "lucide-mui"
import React, { useState } from "react"

import {
  BG_BLACK,
  BG_DARK_HOVER,
  BG_DARK_SELECTED,
  BG_DARK_SELECTED_HOVER,
  BORDER_DARK,
  BORDER_MUTED,
} from "@/constants/colors"
import { AVAILABLE_LOCALES } from "@/constants/locales"
import { useLocale } from "@/contexts/locale"
import i18n from "@/i18n"

type Variant = "dark" | "light"

const TRIGGER_BASE =
  "flex h-[30px] min-w-[70px] items-center justify-between gap-[8px] rounded-[5px] border border-solid px-[10px] py-[6px] text-[12px] font-normal leading-none outline-none transition-colors focus-visible:border-[#2596fa] focus-visible:ring-2 focus-visible:ring-[#2596fa]/40"

const TRIGGER_BY_VARIANT: Record<Variant, string> = {
  dark: "bg-black border-white/40 text-gray-300",
  light: "bg-white border-gray-300 text-gray-700",
}

const CHEVRON_BY_VARIANT: Record<Variant, string> = {
  dark: "h-[12px] w-[12px] text-gray-300",
  light: "h-[12px] w-[12px] text-gray-700",
}

const MENU_PAPER_SX_DARK = {
  backgroundColor: BG_BLACK,
  color: BORDER_MUTED,
  border: `1px solid ${BORDER_DARK}`,
  borderRadius: "5px",
  boxShadow: "0px 4px 4px -2px rgba(0,0,0,0.5), 0px 10px 15px -3px rgba(0,0,0,0.6)",
  "& .MuiMenuItem-root": {
    fontSize: "12px",
    color: BORDER_MUTED,
    "&:hover": {
      backgroundColor: BG_DARK_HOVER,
    },
    "&.Mui-selected": {
      backgroundColor: BG_DARK_SELECTED,
      color: "#ffffff",
      "&:hover": {
        backgroundColor: BG_DARK_SELECTED_HOVER,
      },
    },
  },
} as const

const MENU_PAPER_SX_LIGHT = {
  backgroundColor: "#ffffff",
  color: "#374151",
  border: "1px solid #d1d5db",
  borderRadius: "5px",
  boxShadow: "0px 4px 4px -2px rgba(0,0,0,0.1), 0px 10px 15px -3px rgba(0,0,0,0.15)",
  "& .MuiMenuItem-root": {
    fontSize: "12px",
    color: "#374151",
    "&:hover": {
      backgroundColor: "#f3f4f6",
    },
    "&.Mui-selected": {
      backgroundColor: "#e5e7eb",
      color: "#111827",
      "&:hover": {
        backgroundColor: "#d1d5db",
      },
    },
  },
} as const

const MENU_PAPER_SX_BY_VARIANT: Record<Variant, Record<string, unknown>> = {
  dark: MENU_PAPER_SX_DARK,
  light: MENU_PAPER_SX_LIGHT,
}

type MenuPlacement = "below" | "above"

type Props = {
  variant?: Variant
  menuPlacement?: MenuPlacement
}

const PublicLanguageDropdown: React.FC<Props> = ({ variant = "dark", menuPlacement = "below" }) => {
  const { locale, setLocale } = useLocale()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelectLocale = (selectedLocale: string) => {
    setLocale(selectedLocale)
    handleClose()
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`${TRIGGER_BASE} ${TRIGGER_BY_VARIANT[variant]}`}
        data-testid="public-venue-language-dropdown-btn"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span>{locale.toUpperCase()}</span>
        <ChevronDown className={CHEVRON_BY_VARIANT[variant]} />
      </button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        data-testid="public-venue-language-dropdown-menu"
        anchorOrigin={
          menuPlacement === "above"
            ? { vertical: "top", horizontal: "right" }
            : { vertical: "bottom", horizontal: "right" }
        }
        transformOrigin={
          menuPlacement === "above"
            ? { vertical: "bottom", horizontal: "right" }
            : { vertical: "top", horizontal: "right" }
        }
        slotProps={{ paper: { sx: MENU_PAPER_SX_BY_VARIANT[variant] } }}
      >
        {AVAILABLE_LOCALES.map((loc) => (
          <MenuItem
            key={loc}
            onClick={() => handleSelectLocale(loc)}
            selected={loc === locale}
            className="min-w-[120px]"
            data-testid={`public-venue-language-dropdown-option-${loc}`}
          >
            {i18n.t(`app.language_selector.languages.${loc}`)}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default PublicLanguageDropdown
